import { Link } from "react-router-dom";
import { IMAGES, REGIONS } from "@/lib/site";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { MapPin } from "lucide-react";

const originDestination = [
  { origin: "India", to: "GCC · Africa · SE Asia", commodity: "Pulses, Sesame" },
  { origin: "Canada", to: "Middle East · India · EU", commodity: "Lentils, Peas, Chick Peas" },
  { origin: "Australia", to: "India · Pakistan · GCC", commodity: "Chick Peas, Lentils" },
  { origin: "Africa", to: "GCC · Asia · China", commodity: "Pigeon Peas, Sesame, Chick Peas, Mung Beans, Pulses" },
  { origin: "Brazil", to: "Asia · GCC · China", commodity: "Black Matpe, Beans, Sesame" },
];

export default function GlobalPresence() {
  return (
    <main data-testid="global-presence-page">
      <PageHero
        overline="Global Presence"
        title={
          <>
            Trade flows
            <br />
            <em className="not-italic text-[#2A4D3B]">across six continents.</em>
          </>
        }
        subtitle="From the UAE we coordinate sourcing in over twenty origins and supply buyers in more than twenty-five countries — across the full pulses and oilseeds complex."
        image={IMAGES.shipping}
      />

      <section
        data-testid="map-section"
        className="relative px-6 sm:px-12 lg:px-24 py-24 bg-[#1A1A1A] text-[#FDFBF7] overflow-hidden"
      >
        <img
          src={IMAGES.network}
          alt="Global network"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/70 via-[#1A1A1A]/40 to-[#1A1A1A]/90" />

        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <Overline dark>Where we operate</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.05]">
              Key regions on our trading map.
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REGIONS.map((r, i) => (
              <FadeIn key={r.region} delay={i * 0.06}>
                <div className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/15 p-6 hover:bg-white/10 transition-colors">
                  <MapPin className="h-5 w-5 text-[#D8A48F]" />
                  <p className="mt-5 font-serif-display text-xl">{r.region}</p>
                  <p className="mt-2 text-sm text-[#C8C2B5]">{r.note}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <Section testid="trade-lanes" className="bg-[#FDFBF7]">
        <div className="max-w-3xl mb-14">
          <Overline>Active trade lanes</Overline>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
            From origin to destination — our most active flows.
          </h2>
        </div>

        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full min-w-[640px] sm:min-w-0 text-left">
            <thead>
              <tr className="text-xs uppercase tracking-[0.18em] text-[#4A4540] border-b border-[#EAE7E0]">
                <th className="py-5 pr-6 font-semibold">Origin</th>
                <th className="py-5 pr-6 font-semibold">Destination regions</th>
                <th className="py-5 pr-6 font-semibold">Primary commodities</th>
              </tr>
            </thead>
            <tbody>
              {originDestination.map((row) => (
                <tr
                  key={row.origin}
                  data-testid={`trade-lane-${row.origin.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="border-b border-[#EAE7E0] hover:bg-[#F6F4F0]/50 transition-colors"
                >
                  <td className="py-6 pr-6 font-serif-display text-xl text-[#1A1A1A]">{row.origin}</td>
                  <td className="py-6 pr-6 text-sm text-[#4A4540]">{row.to}</td>
                  <td className="py-6 pr-6 text-sm text-[#2A4D3B] font-medium">{row.commodity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-14">
          <Link
            to="/contact"
            data-testid="global-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#2A4D3B] hover:bg-[#3A634E] text-[#FDFBF7] px-6 py-3.5 text-sm font-semibold transition-colors"
          >
            Discuss a trade lane →
          </Link>
        </div>
      </Section>
    </main>
  );
}
